import { z } from "zod";

/** Le relais la presente a chaque connexion. Differente de celle du serveur : la regie dit « relais a reinstaller », et aucun ordre ne part. */
export const VERSION_CONTRAT_RELAIS = 1;

/**
 * Le nom de la collection OBS que Castellan pilote. La description le porte (`obs-collection-donnees.ts`) et la regie le
 * compare a la collection courante d'OBS pour dire « sur une autre collection » : il est ecrit ici et nulle part ailleurs.
 *
 * Le schema de la description n'accepte que lui (`z.literal`), et le relais compare la collection courante d'OBS au nom
 * de la description : un Castellan qui servirait « Sunset » ne ferait donc jamais piloter la collection de Nicolas.
 */
export const NOM_COLLECTION = "Heure dorée";

/** Le profil d'OBS du lot, a cote de sa collection. Meme regle : le schema de la description n'accepte que lui, jamais `Untitled`. */
export const NOM_PROFIL = "Heure dorée";

export const CANEVAS = ["horizontal", "vertical"] as const;
export const canevasSchema = z.enum(CANEVAS);
export type Canevas = z.infer<typeof canevasSchema>;

export const SCENES = [
  "ouverture",
  "discussion",
  "jeu",
  "coop",
  "code",
  "pause",
  "fin",
  "raid",
  "replay",
  "jeux_commu",
  "incident",
  "compagnie",
  "ecran",
] as const;
export const sceneIdSchema = z.enum(SCENES);
export type SceneId = z.infer<typeof sceneIdSchema>;

export const PLACES_CAM = ["haut_droite", "haut_gauche", "bas_gauche", "cachee"] as const;
export const placeCamSchema = z.enum(PLACES_CAM);
export type PlaceCam = z.infer<typeof placeCamSchema>;

/**
 * Les zones ou aucun texte ne tombe, en vertical (spec, section 3) : l'interface de TikTok et le chat de Twitch en bas, les
 * boutons de TikTok a droite. Valeurs de depart : les maquettes, puis une capture reelle de chaque appli, les fixent.
 */
export const ZONES_LIBRES_VERTICAL = [
  { nom: "bas", x: 0, y: 1500, largeur: 1080, hauteur: 420 },
  { nom: "droite", x: 930, y: 800, largeur: 150, hauteur: 1120 },
] as const;

/** Une seule ligne, sans caractere de controle ni formatage invisible : la regie l'affiche. */
const UNE_LIGNE = /^[^\p{C}\p{Zl}\p{Zp}]+$/u;
const texteCourt = z.string().min(1).max(100).regex(UNE_LIGNE);
export const nomSourceSchema = z.string().min(1).max(60).regex(UNE_LIGNE);
const empreinteSchema = z.string().regex(/^[0-9a-f]{64}$/);

// Chaque branche est stricte : un champ de plus (une requete brute, par exemple) est un refus, pas un champ ignore.
const ordreScene = z.strictObject({
  ordre: z.literal("scene"),
  canevas: canevasSchema,
  scene: sceneIdSchema,
});
const ordreCam = z.strictObject({
  ordre: z.literal("cam"),
  scene: z.enum(["jeu", "coop"]),
  place: placeCamSchema,
});
const ordreAfficher = z.strictObject({
  ordre: z.literal("afficher"),
  canevas: canevasSchema,
  scene: sceneIdSchema,
  source: nomSourceSchema,
  visible: z.boolean(),
});
const ordreVolume = z.strictObject({
  ordre: z.literal("volume"),
  source: nomSourceSchema,
  pourcent: z.number().int().min(0).max(100),
});
const ordreMicro = z.strictObject({ ordre: z.literal("micro"), coupe: z.boolean() });
const ordreReplay = z.strictObject({ ordre: z.literal("replay"), canevas: canevasSchema });
const ordreEnregistrement = z.strictObject({
  ordre: z.literal("enregistrement"),
  canevas: canevasSchema,
  action: z.enum(["demarrer", "arreter", "chapitre"]),
  titre: z.string().min(1).max(60).regex(UNE_LIGNE).optional(),
});
/** `cle` a `null` : retirer la cle. Elle ne vit qu'en memoire : jamais en base, jamais dans un journal. */
const ordreCleTiktok = z.strictObject({
  ordre: z.literal("cle_tiktok"),
  cle: z
    .string()
    .regex(/^[\x21-\x7e]{8,512}$/)
    .nullable(),
});
const ordreAppliquer = z.strictObject({ ordre: z.literal("appliquer"), version: empreinteSchema });

export const corpsOrdreSchema = z.discriminatedUnion("ordre", [
  ordreScene,
  ordreCam,
  ordreAfficher,
  ordreVolume,
  ordreMicro,
  ordreReplay,
  ordreEnregistrement,
  ordreCleTiktok,
  ordreAppliquer,
]);
export type CorpsOrdre = z.infer<typeof corpsOrdreSchema>;

/** Ce que `POST /api/obs/ordre` accepte : la cle TikTok et « Appliquer » ont leurs routes, avec leurs gardes. */
export const corpsOrdreRegieSchema = z.discriminatedUnion("ordre", [
  ordreScene,
  ordreCam,
  ordreAfficher,
  ordreVolume,
  ordreMicro,
  ordreReplay,
  ordreEnregistrement,
]);
export type CorpsOrdreRegie = z.infer<typeof corpsOrdreRegieSchema>;

/** Une trame du flux d'ordres. */
export const ordreRelaisSchema = z.strictObject({
  id: z.string().regex(/^[0-9]{1,18}$/),
  corps: corpsOrdreSchema,
});
export type OrdreRelais = z.infer<typeof ordreRelaisSchema>;

export const CAUSES_ORDRE = [
  "source_inconnue",
  "scene_inconnue",
  "collection_autre",
  "obs_injoignable",
  "obs_refus",
  "live_en_cours",
  "delai_depasse",
  "replay_inactif",
  "enregistrement_inactif",
  "canevas_absent",
  "sortie_absente",
  "version_differente",
  "description_absente",
] as const;
export const causeOrdreSchema = z.enum(CAUSES_ORDRE);
export type CauseOrdre = z.infer<typeof causeOrdreSchema>;

/** `POST /api/relais/ordres/{id}/accuse`. */
export const accuseOrdreSchema = z.strictObject({
  issue: z.enum(["fait", "refuse", "echec"]),
  cause: causeOrdreSchema.optional(),
});
export type AccuseOrdre = z.infer<typeof accuseOrdreSchema>;

/** L'issue d'un ordre telle que la regie la lit : celles du relais, plus ce que le serveur constate seul. */
export const ISSUES_ORDRE = [
  "fait",
  "refuse",
  "echec",
  "relais_absent",
  "relais_a_reinstaller",
  "sans_reponse",
] as const;
export const issueOrdreSchema = z.enum(ISSUES_ORDRE);
export type IssueOrdre = z.infer<typeof issueOrdreSchema>;

/** `POST /api/relais/etat` : ce que le relais sait d'OBS, a chaque changement et toutes les 30 s. */
export const etatObsSchema = z.strictObject({
  relais: z.strictObject({ version: texteCourt, contrat: z.number().int().positive() }),
  obs: z.strictObject({
    joignable: z.boolean(),
    raison: z.enum(["ferme", "mot_de_passe"]).nullable(),
    versionObs: texteCourt.nullable(),
    versionWebsocket: texteCourt.nullable(),
    versionAitum: texteCourt.nullable(),
  }),
  collection: z.strictObject({
    courante: texteCourt.nullable(),
    profil: texteCourt.nullable(),
    versionAppliquee: empreinteSchema.nullable(),
  }),
  scenes: z.strictObject({ horizontal: texteCourt.nullable(), vertical: texteCourt.nullable() }),
  live: z.boolean(),
  enregistrement: z.strictObject({
    horizontal: z.strictObject({
      actif: z.boolean(),
      dureeMs: z.number().int().nonnegative().nullable(),
    }),
    vertical: z.strictObject({ actif: z.boolean() }),
  }),
  replayBuffer: z.boolean(),
  sortiesVerticales: z.strictObject({ actives: z.boolean(), backtrack: z.boolean() }),
  spotifyTrouve: z.boolean(),
});
export type EtatObs = z.infer<typeof etatObsSchema>;

/** Ce qu'obs-websocket ne sait pas creer : la regie les liste, chacun renvoie a son guide. */
export const MANQUES = [
  "canevas_vertical",
  "transition_herse",
  "sortie_tiktok",
  "sortie_youtube",
  "webcam",
  "micro",
  "ecran_milieu",
  "ecran_brut",
  "fichier_decor",
] as const;
export const manqueSchema = z.enum(MANQUES);

/** `POST /api/relais/appliquer/rapport`. */
export const rapportAppliquerSchema = z.strictObject({
  version: empreinteSchema,
  issue: z.enum(["fait", "incomplet", "echec", "refuse"]),
  cause: causeOrdreSchema.optional(),
  dureeMs: z.number().int().nonnegative(),
  crees: z.number().int().nonnegative(),
  regles: z.number().int().nonnegative(),
  horsDescription: z.array(nomSourceSchema).max(100),
  manques: z.array(manqueSchema),
});
export type RapportAppliquer = z.infer<typeof rapportAppliquerSchema>;

/** Une ligne du journal des ordres, telle que la regie la lit. Jamais la cle TikTok : `detail` ne la porte pas. */
export const ligneOrdreSchema = z.object({
  id: z.string(),
  ordre: z.string(),
  origine: z.string(),
  emisLe: z.string(),
  issue: issueOrdreSchema.nullable(),
  cause: z.string().nullable(),
});
export type LigneOrdre = z.infer<typeof ligneOrdreSchema>;

/** `GET /api/obs/etat`. */
export const etatRegieObsSchema = z.object({
  relais: z.enum(["absent", "connecte", "a_reinstaller"]),
  depuis: z.string().nullable(),
  obs: etatObsSchema.nullable(),
  collection: z.object({ versionAttendue: empreinteSchema, aJour: z.boolean() }),
  rapport: rapportAppliquerSchema.nullable(),
  ordres: z.array(ligneOrdreSchema).max(20),
});
export type EtatRegieObs = z.infer<typeof etatRegieObsSchema>;

/** `POST /api/obs/ordre` : l'issue, une fois l'accuse recu ou le delai passe. */
export const reponseOrdreSchema = z.object({
  id: z.string(),
  issue: issueOrdreSchema,
  cause: causeOrdreSchema.nullable(),
});
export type ReponseOrdre = z.infer<typeof reponseOrdreSchema>;
